import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/shared/lib/auth";
import { pageKeyForPath, firstAllowedPath } from "@/shared/lib/pages";

// Feb 2026 — match backend canonical 10-role matrix.
// csm/psm/ism are DISTINCT roles (not folded into management/procurement).
// Legacy roles auto-migrate to the new set.
const ROLE_ALIASES = {
  production_file: "prepress",
  factory:        "production",
  installer:      "ism",
  artist:         "production",
  inventory_staff: "production",
  inventory:      "production",
  procurement:    "psm",
  site_visitor:   "management",
};
const canon = (r) => ROLE_ALIASES[r] || r || "";

export default function ProtectedRoute({ children, roles }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-zinc-500">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  const role = canon(user.role);
  // External clients are strictly portal-only.
  if (role === "client" && !loc.pathname.startsWith("/my-projects")) {
    return <Navigate to="/my-projects" replace />;
  }
  // Designers are funnelled to their privacy-restricted Design Studio,
  // but may also view the shared Floorplan, Pocket Files and the read-only project BOQ.
  if (role === "designer"
      && !loc.pathname.startsWith("/my-designs")
      && !loc.pathname.startsWith("/floorplan")
      && !loc.pathname.startsWith("/pocket-files")
      && !loc.pathname.startsWith("/projects")) {
    return <Navigate to="/my-designs" replace />;
  }
  // Admin sees everything; everyone else must be in `roles`.
  if (roles && roles.length && role !== "admin" && !roles.map(canon).includes(role)) {
    return <Navigate to="/" replace />;
  }
  // Custom user types further restrict to a subset of pages within their base
  // role. allowed_pages is null/undefined for users with no custom type.
  // Pocket Files is a core shared space — never hidden by a custom type.
  if (role !== "admin" && Array.isArray(user.allowed_pages)
      && !loc.pathname.startsWith("/pocket-files")) {
    const key = pageKeyForPath(loc.pathname);
    if (key && !user.allowed_pages.includes(key)) {
      return <Navigate to={firstAllowedPath(user.allowed_pages)} replace />;
    }
  }
  return children;
}
